// src/hooks/useApbsFailureReport.ts

import { ApbsFailureReportResponse, ApbsFailureReportQuery, apbsFailureReportApi, FailedBeneficiariesListResponse, DistrictFailureSummaryResponse } from '..//services/apbs-failure-report.api';
import { useState, useEffect, useCallback, useRef } from 'react';


interface UseApbsFailureReportResult {
  data: ApbsFailureReportResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useApbsFailureReport = (
  params: ApbsFailureReportQuery
): UseApbsFailureReportResult => {
  const [data, setData] = useState<ApbsFailureReportResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchReport = useCallback(async () => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);
      const response = await apbsFailureReportApi.getFailureReport(params);

      // Only update state if this request wasn't cancelled
      if (!controller.signal.aborted) {
        setData(response);
      }
    } catch (err: any) {
      if (!controller.signal.aborted) {
        setError(err?.message || 'Failed to fetch report');
        console.error('Error fetching APBS failure report:', err);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [
    params.startDate,
    params.endDate,
    params.stage,
    params.page,
    params.limit,
  ]);

  useEffect(() => {
    fetchReport();

    return () => {
      // Cleanup: cancel request when params change or component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchReport]);

  return { data, loading, error, refetch: fetchReport };
};

interface UseFailedBeneficiariesResult {
  data: FailedBeneficiariesListResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useFailedBeneficiaries = (
  params: ApbsFailureReportQuery
): UseFailedBeneficiariesResult => {
  const [data, setData] = useState<FailedBeneficiariesListResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchBeneficiaries = useCallback(async () => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);
      const response = await apbsFailureReportApi.getFailedBeneficiaries(params);

      if (!controller.signal.aborted) {
        setData(response);
      }
    } catch (err: any) {
      if (!controller.signal.aborted) {
        setError(err?.message || 'Failed to fetch beneficiaries');
        console.error('Error fetching failed beneficiaries:', err);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [
    params.startDate,
    params.endDate,
    params.stage,
    params.search,
    params.district,
    params.status,
    params.page,
    params.limit,
  ]);

  useEffect(() => {
    fetchBeneficiaries();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchBeneficiaries]);

  return { data, loading, error, refetch: fetchBeneficiaries };
};

interface UseDistrictSummaryResult {
  data: DistrictFailureSummaryResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useDistrictFailureSummary = (
  params: ApbsFailureReportQuery
): UseDistrictSummaryResult => {
  const [data, setData] = useState<DistrictFailureSummaryResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchDistrictSummary = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);
      const response = await apbsFailureReportApi.getDistrictSummary(params);

      if (!controller.signal.aborted) {
        setData(response);
      }
    } catch (err: any) {
      if (!controller.signal.aborted) {
        setError(err?.message || 'Failed to fetch district summary');
        console.error('Error fetching district summary:', err);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [
    params.startDate,
    params.endDate,
    params.stage,
  ]);

  useEffect(() => {
    fetchDistrictSummary();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchDistrictSummary]);

  return { data, loading, error, refetch: fetchDistrictSummary };
};

// Hook for date range management
export const useDateRange = (defaultDays: number = 30) => {
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - defaultDays);
    return date.toISOString().split('T')[0];
  });

  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  const setLast7Days = useCallback(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, []);

  const setLast30Days = useCallback(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, []);

  const setCurrentMonth = useCallback(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(now.toISOString().split('T')[0]);
  }, []);

  return {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    setLast7Days,
    setLast30Days,
    setCurrentMonth,
  };
};
